import { useEffect } from "react";
import { tr } from "../../i18n/tr";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { BlockRenderer, THEMES } from "../../pages/formation/BlockEditor";
import { ScormPlayer } from "../lms/ScormPlayer";

/** Mode projection plein écran (vidéoprojecteur / TV) : un module à la fois, navigation ← →, Échap pour quitter. */
export function ProjectionMode({ api, sequences, index, moduleData, onIndex, onExit }) {
  const seq = sequences[index];
  const theme = THEMES[moduleData?.module?.theme || "blitzz"] || THEMES.blitzz;

  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {});
    const onKey = (e) => {
      if (e.key === "Escape") onExit();
      if (e.key === "ArrowRight") onIndex(Math.min(sequences.length - 1, index + 1));
      if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };
  }, [index, sequences.length, onExit, onIndex]);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: theme.bg }} data-testid="projection-mode">
      <div className="flex items-center gap-3 px-6 py-3 border-b border-white/10 bg-black/30">
        <Maximize2 className="w-4 h-4" style={{ color: "var(--brand-primary, #f7c935)" }} />
        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: theme.muted }}>Projection · {seq?.lesson_title ? seq.lesson_title + " · " : ""}Séquence {index + 1}/{sequences.length}</div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => onIndex(Math.max(0, index - 1))} disabled={index === 0} className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-30" data-testid="projection-prev"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => onIndex(Math.min(sequences.length - 1, index + 1))} disabled={index >= sequences.length - 1} className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-30" data-testid="projection-next"><ChevronRight className="w-5 h-5" /></button>
          <button onClick={onExit} className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20" data-testid="projection-exit"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-8 py-10">
          <h1 className="text-4xl lg:text-5xl font-black mb-8" style={{ color: theme.heading }} data-testid="projection-title">{moduleData?.module?.title || seq?.title}</h1>
          {moduleData?.module?.kind === "scorm" ? (
            <ScormPlayer sequence={{ sequence_id: moduleData.module.module_id, scorm_entry: moduleData.module.scorm_entry, scorm_version: moduleData.module.scorm_version, title: moduleData.module.title }} enrollmentId={null} api={api} />
          ) : (moduleData?.blocks || []).length === 0 ? (
            <div className="text-lg italic" style={{ color: theme.muted }}>{tr("lms.ce_module_n_a_bdf0", "Ce module n'a pas encore de contenu.")}</div>
          ) : (
            <div className="space-y-6 text-lg">
              {(moduleData.blocks || []).map((b, i) => <div key={i}><BlockRenderer block={b} theme={theme} /></div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectionMode;
